// src/components/monthly/ApplyForms/InvoiceComponent.tsx
import React, { useState, useEffect } from 'react';
import { differenceInCalendarDays, format } from 'date-fns';
import { getRoomTypeDetail } from '@/API/apiService';
import { useParams } from 'react-router-dom';

export interface InvoiceComponentProps {
  estimateData: {
    fromDate: string;
    toDate: string;
    guests: string;
  };
  // applyDate（申込日）はオプショナル。渡されなければ今日の日付を使用する
  applyDate?: string;
}

/**
 * 指定された fromDate と toDate の間を月ごとに分割する関数
 */
const getMonthlySegments = (fromDate: Date, toDate: Date) => {
  const segments: {
    start: Date;
    end: Date;
    totalDays: number;
    segmentDays: number;
    month: number;
    year: number;
  }[] = [];
  let current = new Date(fromDate);

  while (current <= toDate) {
    const year = current.getFullYear();
    const month = current.getMonth(); // 0-indexed
    const endOfMonth = new Date(year, month + 1, 0);
    // セグメント終了日は、当月最終日と toDate のうち早い方
    const segmentEnd = endOfMonth > toDate ? toDate : endOfMonth;
    const totalDays = endOfMonth.getDate();
    const segmentDays = differenceInCalendarDays(segmentEnd, current) + 1;
    segments.push({
      start: new Date(current),
      end: new Date(segmentEnd),
      totalDays,
      segmentDays,
      month: month + 1,
      year: year,
    });
    // 次のセグメントの開始日は翌月の1日
    current = new Date(year, month + 1, 1);
  }
  return segments;
};

export const InvoiceComponent: React.FC<InvoiceComponentProps> = ({
  estimateData,
  applyDate,
}) => {
  const [roomDetail, setRoomDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { property_id, room_id } = useParams();

  useEffect(() => {
    getRoomTypeDetail(String(property_id), String(room_id))
      .then((data) => {
        setRoomDetail(data.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [property_id, room_id]);

  if (loading) return <div>Loading...</div>;
  if (error || !roomDetail) return <div>Error loading data.</div>;

  const monthlyPrice: number = roomDetail.prices.monthly_price;
  const fromDateObj = new Date(estimateData.fromDate);
  const toDateObj = new Date(estimateData.toDate);

  const segments = getMonthlySegments(fromDateObj, toDateObj);

  // applyDate が渡されなければ、今日の日付を使用する
  const effectiveApplyDate = applyDate ? new Date(applyDate) : new Date();
  const applyDateObj = effectiveApplyDate;

  // 「入居開始日」の月初（例: 4月11日なら4月1日）を基準として7日以内か判定する
  const effectiveOccupancyStart = new Date(
    fromDateObj.getFullYear(),
    fromDateObj.getMonth(),
    1
  );
  const diffDays = differenceInCalendarDays(
    effectiveOccupancyStart,
    applyDateObj
  );

  // 初回請求のセグメント詳細
  const segmentDetails: { label: string; rent: number }[] = [];

  if (segments.length > 0) {
    // 第一セグメントは常に日割り計算
    const firstSegment = segments[0];
    const rent = Math.floor(
      (firstSegment.segmentDays / firstSegment.totalDays) * monthlyPrice
    );
    const label =
      firstSegment.start.getTime() === firstSegment.end.getTime()
        ? `${firstSegment.month}月分賃料 (${format(firstSegment.start, 'yyyy-MM-dd')})`
        : `${firstSegment.month}月分賃料 (${format(firstSegment.start, 'yyyy-MM-dd')}～${format(firstSegment.end, 'yyyy-MM-dd')})`;
    segmentDetails.push({ label, rent });
  }

  // 次月（occupancy period の2ヶ月目、例では5月分）について
  // 申込日と effectiveOccupancyStart の差が7日以内の場合に表示する
  if (segments.length > 1 && diffDays <= 7) {
    const seg = segments[1];
    const rent =
      seg.segmentDays === seg.totalDays
        ? monthlyPrice
        : Math.floor((seg.segmentDays / seg.totalDays) * monthlyPrice);
    const label =
      seg.start.getTime() === seg.end.getTime()
        ? `${seg.month}月分賃料 (${format(seg.start, 'yyyy-MM-dd')})`
        : `${seg.month}月分賃料 (${format(seg.start, 'yyyy-MM-dd')}～${format(seg.end, 'yyyy-MM-dd')})`;
    segmentDetails.push({ label, rent });
  }

  // 合計家賃は各セグメントの合計
  const totalRent = segmentDetails.reduce((sum, seg) => sum + seg.rent, 0);

  // 初回手数料（家賃の10%を100円未満切り捨て）
  // 退去時クリーニング費用（家賃の20%を100円未満切り捨て）
  const truncateToHundreds = (value: number) => Math.floor(value / 100) * 100;
  const initialFee = truncateToHundreds(monthlyPrice * 0.1);
  const cleaningFee = truncateToHundreds(monthlyPrice * 0.2);

  const totalAmount = totalRent + initialFee + cleaningFee;

  return (
    <div className="max-w-xl mx-auto bg-whiteYellow text-sm md:text-base my-8">
      {/* タイトル */}
      <div className="flex items-center">
        <span className="w-4 h-4 bg-black mr-2" />
        <h4 className="font-bold text-lg"> ご請求について</h4>
      </div>
      <hr className="border-t-2 border-black mb-5" />

      {/* 初回のご請求 */}
      <section className="mb-10">
        <h2 className="text-lg md:text-xl mb-1">□ 初回のご請求</h2>
        <p className="mb-4 leading-relaxed text-xs md:text-sm ml-3">
          申し込み完了後にお送りするメールに記載のリンク先にて
          クレジットカード情報の入力が完了し次第ご請求いたします。
        </p>

        <div className="border border-black">
          <div className="space-y-2 p-1 text-xs md:text-sm">
            {segmentDetails.map((seg, idx) => (
              <div className="flex" key={idx}>
                <div className="w-4/5">{seg.label}</div>
                <div className="w-1/5 text-right">
                  {seg.rent.toLocaleString()}円
                </div>
              </div>
            ))}
            <div className="flex">
              <div className="w-4/5">初回登録料</div>
              <div className="w-1/5 text-right">
                {initialFee.toLocaleString()}円
              </div>
            </div>
            <div className="flex">
              <div className="w-4/5">退去時のクリーニング費用</div>
              <div className="w-1/5 text-right">
                {cleaningFee.toLocaleString()}円
              </div>
            </div>
          </div>
          <div className="border-t border-black p-1 flex justify-end">
            <p>ご請求金額 {totalAmount.toLocaleString()}円</p>
          </div>
        </div>
      </section>

      {/* ２回目以降のご請求 */}
      <section>
        <h2 className="text-lg md:text-xl mb-1">□ ２回目以降のご請求</h2>
        <p className="mb-4 leading-relaxed ml-3">
          毎月１２日に翌月分の賃料をご請求いたします。
        </p>
        <div className="border border-black p-1 flex justify-between items-center">
          <p>ご請求金額</p>
          <p className="text-lg md:text-xl">¥{monthlyPrice.toLocaleString()}</p>
        </div>
      </section>
    </div>
  );
};

export default InvoiceComponent;
